import { ApiHolder, configApiRef, featureFlagsApiRef } from '@backstage/core-plugin-api';
import type { GithubDestination } from '../../../services/unifiedGithubService/unifiedGithubService.types';

type GithubDestinationResolution =
  { status: 'resolved'; destination: GithubDestination } | { status: 'error'; message: string };

const orgFromUrl = (url: string | undefined): string | undefined =>
  url
    ?.replace(/\/\*\*$/, '')
    .split('/')
    .filter(Boolean)
    .pop() || undefined;

/** Validates template configuration without inferring the publish owner from its source URL. */
export const resolveGithubDestination = ({
  configuredOrganization,
  ghecOrg,
  emuOrgUrl,
  emuAuthEnabled,
}: {
  configuredOrganization: unknown;
  ghecOrg: string;
  emuOrgUrl?: string;
  emuAuthEnabled: boolean;
}): GithubDestinationResolution => {
  if (configuredOrganization === undefined || configuredOrganization === null) {
    return { status: 'resolved', destination: { organization: ghecOrg, origin: 'ghec' } };
  }

  const requested = typeof configuredOrganization === 'string' ? configuredOrganization.trim() : '';
  const emuOrg = orgFromUrl(emuOrgUrl);
  const supported = [ghecOrg, emuOrg].filter((org): org is string => Boolean(org));

  if (!requested) {
    return {
      status: 'error',
      message: `This template has an invalid GitHub organization setting. Supported organizations: ${supported.join(', ')}. Contact the template owner.`,
    };
  }

  if (requested.toLowerCase() === ghecOrg.toLowerCase()) {
    return { status: 'resolved', destination: { organization: ghecOrg, origin: 'ghec' } };
  }

  if (emuOrg && requested.toLowerCase() === emuOrg.toLowerCase()) {
    if (!emuAuthEnabled) {
      return {
        status: 'error',
        message: `This template creates repositories in ${emuOrg}. Turn on "Enable GitHub EMU Authentication" (github-emu-auth) in Settings > Feature Flags, refresh the page, and sign in with your EMU account.`,
      };
    }
    return { status: 'resolved', destination: { organization: emuOrg, origin: 'emu' } };
  }

  return {
    status: 'error',
    message: `This template targets GitHub organization "${requested}", which Runway does not support in this environment. Supported organizations: ${supported.join(', ')}. Contact the template owner.`,
  };
};

/** Shares app configuration between the field and its non-React validator. */
export const getGithubDestination = (
  configuredOrganization: unknown,
  apiHolder?: ApiHolder,
): GithubDestinationResolution => {
  const configApi = apiHolder?.get(configApiRef);
  return resolveGithubDestination({
    configuredOrganization,
    ghecOrg: configApi?.getOptionalString('github.ghecOrg') ?? 'AAInternal',
    emuOrgUrl: configApi?.getOptionalString('catalog.emuOrgUrl'),
    emuAuthEnabled: apiHolder?.get(featureFlagsApiRef)?.isActive('github-emu-auth') ?? false,
  });
};
